
import { AtarimEditorRef } from "editor";
import { CircleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { ResponsiveDialog } from "ui";
import { FileUploadIcon } from "../../../icons/FileUploadIcon";
import { getCurrentTaskState, getGeneralTaskModalOpen, getSelectedTaskId, setGeneralTaskModalOpen, setIsAIActive } from "../../../store/tasksSlice";
import { ActionBar } from "../../tasks/create-modal/action-bar/action-bar";
import Activity from "../../tasks/create-modal/activity/activity";
import PhotoEditor from "../../tasks/create-modal/annotate/annotate-editor";
import { CommentBox } from "../../tasks/create-modal/comment/comment-box";
import FileDisplay from "../../tasks/create-modal/file/file";
import { Header } from "../../tasks/create-modal/header/header";
import { useCreateTask } from "../../tasks/createTask";
import { TypingIndicator } from "../../tasks/typing-indicator";

export const GeneralTaskModal = () => {
    const dispatch = useDispatch();
    const { isPending } = useSelector(getCurrentTaskState);
    const isModalOpen = useSelector(getGeneralTaskModalOpen);
    const taskId = useSelector(getSelectedTaskId);
    const [files, setFiles] = useState<File[]>([]);
    const [comment, setComment] = useState<string>('');
    const { handleCreate, isLoading, isSuccess } = useCreateTask({
        existingTask: !isPending,
        taskType: 'General',
        setFiles,
        setComment
    });
    const editorRef = useRef<AtarimEditorRef | null>(null);

    const toggleGeneralTaskModal = (open: boolean) => {
        dispatch(setGeneralTaskModalOpen(open));
    };

    const onDrop = (acceptedFiles: File[]) => {
        setFiles(acceptedFiles);
    };

    const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
        noClick: true,
        onDrop,
        // Prevent the default window drop behavior
        preventDropOnDocument: true,
        // Only accept specific file types if needed in the future
        // accept: {'image/*': [], 'application/pdf': []},
        onDragOver: (e) => {
            // Allow scroll events to propagate
            if ((e.target as HTMLElement).closest('[data-scrollable]')) {
                e.stopPropagation();
            }
        },
    });

    useEffect(() => {
        if (isModalOpen) {
            dispatch(setIsAIActive(false))
        }
    }, [isModalOpen, dispatch]);


    return (
        <ResponsiveDialog
            open={!!isModalOpen}
            onOpenChange={toggleGeneralTaskModal}
        >
            <ResponsiveDialog.Content
                aria-describedby={undefined}
                showClose={false}
                className="p-2 gap-2 z-20 overflow-auto bg-transparent border-none shadow-none"
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
            >
                <ResponsiveDialog.Title className="sr-only fixed">
                    General Task
                </ResponsiveDialog.Title>
                <div
                    {...getRootProps()}
                    className="mx-auto w-full md:w-[400px] relative rounded-md flex flex-col gap-2"
                >
                    <input {...getInputProps()} />
                    {isDragActive && (
                        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background rounded-lg border-2 border-dashed border-purple-300">
                            <FileUploadIcon size={150} />
                            <p className="text-sm text-gray-600 text-center">
                                Drop your files here<br />
                                to start upload
                            </p>
                        </div>
                    )}
                    <div className="space-y-2 bg-background p-2.5 rounded-2xl">
                        <Header />
                        <Activity />
                        <div className="flex flex-col gap-1 relative pt-1">
                            {taskId && <TypingIndicator taskId={taskId} />}
                            <CommentBox
                                fileOpen={open}
                                editorRef={editorRef}
                                handleCreate={handleCreate}
                                isLoading={isLoading}
                                isSuccess={isSuccess}
                                files={files}
                                setFiles={setFiles}
                                comment={comment}
                                setComment={setComment}
                            />
                        </div>
                        {files.length > 0 && (
                            <>
                                <div className="flex flex-col gap-2 max-h-[135px] overflow-auto">
                                    <div className="grid grid-cols-5 gap-2">
                                        {files
                                            .filter(f => f.type.startsWith('image/'))
                                            .map((f) => (
                                                <FileDisplay file={f} removable key={f.name} setFiles={setFiles} isPendingFile={true} />
                                            ))}
                                    </div>
                                    <div className="grid grid-cols-1 gap-2">
                                        {files
                                            .filter(f => !f.type.startsWith('image/'))
                                            .map((f) => (
                                                <FileDisplay file={f} removable key={f.name} setFiles={setFiles} isPendingFile={true} />
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}
                        <ActionBar
                            editorRef={editorRef}
                            handleCreate={handleCreate}
                            isLoading={isLoading}
                            files={files}
                            comment={comment}
                            setComment={setComment}
                            setFiles={setFiles}
                        />
                    </div>
                    <div className="w-fit mx-auto bg-background px-2 py-2 rounded-full flex justify-center items-center text-textcolor  text-xs">
                        <CircleAlert size={18} className='mr-1' />General tasks are not attached to elements on the page
                    </div>
                </div>
                <PhotoEditor setFiles={setFiles} files={files} />
            </ResponsiveDialog.Content>
        </ResponsiveDialog>
    );
};